import { useEffect } from 'react';

/**
 * iOS (app instalado na Tela de Início): diagnóstico passivo da tela.
 *
 * A barra de status agora é opaca (apple-mobile-web-app-status-bar-style = black),
 * eliminando os bugs do WebKit 301108 / 297779 que só ocorriam com black-translucent.
 * Com a barra opaca, a altura normal do app é a tela menos a barra de status, então
 * este hook NÃO altera nada na página: apenas mede e grava em window.__viewportFixState
 * para a tela de Diagnóstico.
 */

export type ViewportFixState = {
  lastCheck: number;
  reason: string;
  innerHeight: number;
  screenHeight: number;
  vvHeight: number | null;
  safeAreaTop: number;
  portrait: boolean;
  statusBarMode: 'translúcida' | 'opaca';
};

declare global {
  interface Window {
    __viewportFixState?: ViewportFixState;
  }
}

const isLandscape = () =>
  window.matchMedia?.('(orientation: landscape)').matches ?? window.innerWidth > window.innerHeight;

const isIosDevice = () => {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS se apresenta como Mac com toque.
  return /Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
};

export function useIosStandaloneViewportFix() {
  useEffect(() => {
    const nav = window.navigator as Navigator & { standalone?: boolean };
    if (nav.standalone !== true || !isIosDevice()) return;

    const vv = window.visualViewport;
    let raf = 0;

    const probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none';
    document.body.appendChild(probe);

    const measure = (reason: string) => {
      raf = 0;
      const safeAreaTop = Math.round(probe.getBoundingClientRect().height);
      const portrait = !isLandscape();
      const state: ViewportFixState = {
        lastCheck: Date.now(),
        reason,
        innerHeight: window.innerHeight,
        screenHeight: window.screen.height,
        vvHeight: vv ? Math.round(vv.height) : null,
        safeAreaTop,
        portrait,
        statusBarMode: portrait && safeAreaTop > 0 ? 'translúcida' : 'opaca',
      };
      window.__viewportFixState = state;
      console.info('[viewport-fix]', reason, state);
    };

    const schedule = (reason: string) => () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => measure(reason));
    };

    const onResize = schedule('resize');
    const onPageShow = schedule('pageshow');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') schedule('visibilitychange')();
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);

    schedule('montagem')();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
      probe.remove();
    };
  }, []);
}
